import { z } from "zod";

export const studioStatusSchema = z.enum(["pending_approval", "active", "suspended", "rejected"]);
export const studioSchema = z.object({
  id: z.uuid(), handle: z.string().min(1), name: z.string().min(1), email: z.email(), phone: z.string().nullable(),
  status: studioStatusSchema, created_at: z.iso.datetime()
}).strict();
export const studioResponseSchema = z.object({ result: z.array(studioSchema) }).strict();

const moneyValueSchema = z.union([z.string().regex(/^\d+$/), z.number().int().nonnegative()]);
export const payoutSchema = z.object({
  id: z.uuid(), studio_id: z.uuid(), state: z.enum(["requested", "under_review"]), amount_minor: moneyValueSchema,
  currency_code: z.enum(["THB", "KRW", "USD"]), requested_at: z.iso.datetime()
}).strict();
export const refundSchema = z.object({
  id: z.uuid(), order_id: z.uuid(), customer_id: z.uuid(), state: z.enum(["requested", "under_review"]),
  amount_minor: moneyValueSchema, currency_code: z.enum(["THB", "KRW", "USD"]), reason: z.string().min(1).max(1000), requested_at: z.iso.datetime()
}).strict();
export const financeResponseSchema = z.object({ result: z.object({ payouts: z.array(payoutSchema), refunds: z.array(refundSchema) }).strict() }).strict();

const operationsSummarySchema = z.object({
  registeredCustomers: z.number().int().nonnegative(),
  pendingStudios: z.number().int().nonnegative(),
  activeStudios: z.number().int().nonnegative(),
  activeProjects: z.number().int().nonnegative(),
  stalledProjects: z.number().int().nonnegative(),
  queuedAiJobs: z.number().int().nonnegative(),
  failedAiJobs24h: z.number().int().nonnegative(),
  openAftercare: z.number().int().nonnegative()
}).strict();
const stalledProjectSchema = z.object({
  id: z.uuid(), name: z.string().min(1), state: z.string().min(1), studio_id: z.uuid().nullable(), updated_at: z.iso.datetime()
}).strict();
const floorPlanJobSchema = z.object({
  id: z.uuid(), project_id: z.uuid(), state: z.enum(["queued", "processing", "failed"]), error_code: z.string().nullable(),
  attempts: z.number().int().nonnegative(), requested_at: z.iso.datetime(), updated_at: z.iso.datetime()
}).strict();
export const operationsResponseSchema = z.object({ result: z.object({
  summary: operationsSummarySchema,
  stalledProjects: z.array(stalledProjectSchema),
  floorPlanJobs: z.array(floorPlanJobSchema)
}).strict() }).strict();

const jsonObjectSchema = z.record(z.string(), z.unknown()).nullable();
const auditEventSchema = z.object({
  id: z.uuid(), actor_id: z.uuid().nullable(), event_type: z.string().min(1), target_type: z.string().min(1), target_id: z.string().min(1),
  reason: z.string().nullable(), before_value: jsonObjectSchema, after_value: jsonObjectSchema, correlation_id: z.uuid(), created_at: z.iso.datetime()
}).strict();
export const auditResponseSchema = z.object({ result: z.array(auditEventSchema) }).strict();

export type StudioStatus = z.infer<typeof studioStatusSchema>;
export type StudioReview = z.infer<typeof studioSchema>;
export type PayoutReview = z.infer<typeof payoutSchema>;
export type RefundReview = z.infer<typeof refundSchema>;
export type OperationsOverview = z.infer<typeof operationsResponseSchema>["result"];
export type AuditEvent = z.infer<typeof auditEventSchema>;

export function maskEmail(email: string): string {
  const [local, domain] = email.split("@");
  if (local === undefined || domain === undefined) return "••••";
  return `${local.slice(0, 2)}${"•".repeat(Math.max(2, local.length - 2))}@${domain}`;
}

export function maskPhone(phone: string | null): string {
  if (phone === null || phone.length < 4) return "등록 없음";
  return `${"•".repeat(Math.max(4, phone.length - 4))}${phone.slice(-4)}`;
}

export function shortId(value: string): string {
  return `${value.slice(0, 8)}…${value.slice(-4)}`;
}

export function formatMinorAmount(value: string | number, currency: "THB" | "KRW" | "USD"): string {
  const amount = typeof value === "number" ? BigInt(value) : BigInt(value);
  const exponent = currency === "KRW" ? 0 : 2;
  const divisor = 10n ** BigInt(exponent);
  const whole = amount / divisor;
  const fraction = exponent === 0 ? "" : `.${(amount % divisor).toString().padStart(exponent, "0")}`;
  return `${whole.toLocaleString("en-US")}${fraction} ${currency}`;
}
